import { FC } from 'react';

import { Link } from '@inertiajs/react';

import { Card, CardContent } from '../../components/ui/Card';
import { Image } from '../../components/ui/Image';
import { Universe } from '../../types';
import { cn } from '../../utils';

interface SellerCatalogNavigationUniverseItem {
    href: string;
    image?: string;
    name: string;
    slug: Universe['slug'];
    active?: boolean;
    testId?: string;
}

interface Props {
    universes?: SellerCatalogNavigationUniverseItem[];
    sectionTestId: string;
}

export const SellerCatalogNavigation: FC<Props> = ({
    universes,
    sectionTestId,
}) => {
    const hasUniverseNavigation = (universes?.length ?? 0) > 1;

    if (!hasUniverseNavigation) {
        return null;
    }

    return (
        <section className="pb-1 sm:pb-2" data-testid={sectionTestId}>
            <div className="container px-4 sm:px-6 lg:px-8">
                <div
                    className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-6"
                    data-testid={`${sectionTestId}-universes`}
                >
                    {universes?.map((universe) => (
                        <Link
                            key={universe.href}
                            href={universe.href}
                            aria-current={universe.active ? 'page' : undefined}
                            data-testid={universe.testId}
                            className="group block"
                        >
                            <Card
                                className={cn(
                                    'h-full rounded-2xl border transition-all dark:border-0 dark:bg-transparent sm:dark:border sm:dark:bg-card',
                                    universe.active
                                        ? 'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/10'
                                        : 'border-border/70 hover:border-primary/30 hover:shadow-sm',
                                )}
                            >
                                <CardContent className="flex items-center justify-between gap-3 p-3">
                                    <div className="min-w-0">
                                        <p className="line-clamp-2 text-sm leading-5 font-semibold">
                                            {universe.name}
                                        </p>
                                    </div>

                                    <div className="h-12 w-14 shrink-0 overflow-hidden rounded-xl bg-muted/30">
                                        {universe.image ? (
                                            <Image
                                                src={universe.image}
                                                alt={universe.name}
                                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                            />
                                        ) : (
                                            <div className="h-full w-full bg-gradient-to-br from-primary/10 via-muted/40 to-muted/60" />
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
};
